import React, { useRef, useState, useCallback, useEffect } from 'react';
import api from '../services/api';
import { Trash2 } from 'lucide-react';

const CATEGORIAS = [
  { key: 'foto_local_padrao',    label: 'Padrão'    },
  { key: 'foto_local_disjuntor', label: 'Disjuntor' },
  { key: 'foto_local_medidor',   label: 'Medidor'   },
  { key: 'foto_local_telhado',   label: 'Telhado'   },
  { key: 'foto_local_eletrica',  label: 'Elétrica'  },
  { key: 'foto_local_outros',    label: 'Outros'    },
];

const ACCENT = '#34d399';

/* ── FotosLocalPanel ────────────────────────────────────────────────────────
 * Props:
 *   leadId      – ID do lead
 *   readonly    – true = só visualizar
 *   initialOpen – painel começa aberto
 * ─────────────────────────────────────────────────────────────────────────*/
export default function FotosLocalPanel({ leadId, readonly = false, initialOpen = false }) {
  const [open, setOpen]           = useState(initialOpen);
  const [photos, setPhotos]       = useState(null);
  const [loading, setLoading]     = useState(false);
  const [loadErr, setLoadErr]     = useState(false);
  const [activeKey, setActiveKey] = useState(CATEGORIAS[0].key);
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState('');
  const [dragging, setDragging]   = useState(false);
  const [lightbox, setLightbox]   = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const fileRef  = useRef(null);
  // ID único para o <label htmlFor> — stable por leadId
  const inputId  = `fotos-local-${leadId}`;

  /* ── Carregamento ────────────────────────────────────────────────────── */
  const load = async () => {
    if ((photos !== null && !loadErr) || loading) return;
    setLoading(true); setLoadErr(false);
    try {
      const results = await Promise.all(
        CATEGORIAS.map(c =>
          api.get(`/documentos/arquivo-multiplo/list/?lead_id=${leadId}&categoria=${c.key}`)
        )
      );
      const map = {};
      CATEGORIAS.forEach((c, i) => { map[c.key] = results[i].data || []; });
      setPhotos(map);
    } catch {
      setLoadErr(true);
    } finally { setLoading(false); }
  };

  // Carrega automaticamente ao abrir — cobre tanto o toggle normal quanto
  // o caso initialOpen=true
  useEffect(() => {
    if (open) load();
  }, [open]); // eslint-disable-line

  const handleOpen = () => setOpen(prev => !prev);

  /* ── Upload ──────────────────────────────────────────────────────────── */
  const uploadFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    setUploadErr('');
    for (const file of Array.from(files)) {
      try {
        const fd = new FormData();
        fd.append('arquivo', file);
        fd.append('categoria', activeKey);
        fd.append('lead_id', leadId);
        const r = await api.post('/documentos/arquivo-multiplo/upload/', fd);
        setPhotos(prev => ({
          ...prev,
          [activeKey]: [...(prev[activeKey] || []), { id: r.data.id, url: r.data.url }],
        }));
      } catch {
        setUploadErr('Erro ao enviar arquivo. Tente novamente.');
      }
    }
    setUploading(false);
  };

  /* ── Delete ──────────────────────────────────────────────────────────── */
  const handleDeleteRequest = (id) => setDeleteTarget(id);

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    const id = deleteTarget;
    setDeleteTarget(null);
    try {
      await api.delete(`/documentos/arquivo-multiplo/${id}/delete/`);
      setPhotos(prev => ({
        ...prev,
        [activeKey]: prev[activeKey].filter(f => f.id !== id),
      }));
    } catch {
      setUploadErr('Erro ao remover foto.');
    }
  };

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    if (readonly) return;
    uploadFiles(e.dataTransfer.files);
  }, [readonly, leadId, activeKey]); // eslint-disable-line

  /* ── Derivados ───────────────────────────────────────────────────────── */
  const isVideo = (url) => url && /\.(mp4|mov|avi|webm|mkv)$/i.test(url);
  const activeFotos  = photos ? (photos[activeKey] || []) : [];
  const totalCount   = photos
    ? CATEGORIAS.reduce((s, c) => s + (photos[c.key]?.length || 0), 0)
    : 0;
  const activeLabel  = CATEGORIAS.find(c => c.key === activeKey)?.label || '';

  /* ── Render ──────────────────────────────────────────────────────────── */
  return (
    <div style={{ marginTop: 14, borderTop: '1px solid #1a2035', paddingTop: 14 }}>

      {/* Toggle */}
      <button
        type="button"
        onClick={handleOpen}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '8px 12px', borderRadius: 8, cursor: 'pointer',
          background: 'rgba(52,211,153,0.07)', border: '1px solid rgba(52,211,153,0.22)',
          color: ACCENT, fontSize: 13, fontWeight: 700,
        }}
      >
        <span>🏠 Fotos do Local{totalCount > 0 ? ` (${totalCount})` : ''}</span>
        <span style={{ fontSize: 10, opacity: 0.7 }}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div style={{
          marginTop: 8, background: '#080c14',
          border: '1px solid #1a2035', borderRadius: 9, padding: '10px 12px',
        }}>

          {loading && (
            <div style={{ fontSize: 12, color: '#475569', textAlign: 'center', padding: 14 }}>
              Carregando...
            </div>
          )}

          {!loading && loadErr && (
            <div style={{ fontSize: 12, color: '#f87171', textAlign: 'center', padding: 10 }}>
              Erro ao carregar.{' '}
              <button
                onClick={() => { setPhotos(null); load(); }}
                style={{ color: '#60a5fa', background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, textDecoration: 'underline' }}
              >
                Tentar novamente
              </button>
            </div>
          )}

          {!loading && photos !== null && (
            <>
              {/* Pills de categoria */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 10 }}>
                {CATEGORIAS.map(c => {
                  const cnt = photos[c.key]?.length || 0;
                  const active = c.key === activeKey;
                  return (
                    <button
                      key={c.key}
                      type="button"
                      onClick={() => { setActiveKey(c.key); setDeleteTarget(null); setUploadErr(''); }}
                      style={{
                        fontSize: 11, padding: '3px 9px', borderRadius: 20,
                        border: active ? `1px solid ${ACCENT}` : '1px solid #1e293b',
                        background: active ? 'rgba(52,211,153,0.14)' : 'transparent',
                        color: active ? ACCENT : '#64748b',
                        cursor: 'pointer', fontWeight: active ? 700 : 400,
                        whiteSpace: 'nowrap', transition: 'all 0.1s',
                      }}
                    >
                      {c.label}{cnt > 0 ? ` ${cnt}` : ''}
                    </button>
                  );
                })}
              </div>

              {/* Confirmação inline de remoção (substitui window.confirm) */}
              {deleteTarget && (
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                  background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.25)',
                  borderRadius: 7, padding: '7px 10px', marginBottom: 8,
                }}>
                  <span style={{ fontSize: 12, color: '#f87171' }}>Remover esta foto?</span>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button
                      onClick={() => setDeleteTarget(null)}
                      style={{ background: 'transparent', border: '1px solid #334155', borderRadius: 6, padding: '3px 10px', color: '#64748b', fontSize: 11, cursor: 'pointer' }}
                    >Cancelar</button>
                    <button
                      onClick={handleDeleteConfirm}
                      style={{ background: 'rgba(248,113,113,0.2)', border: '1px solid rgba(248,113,113,0.4)', borderRadius: 6, padding: '3px 10px', color: '#f87171', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
                    >Remover</button>
                  </div>
                </div>
              )}

              {/* Grade de fotos da categoria ativa */}
              {activeFotos.length > 0 ? (
                <div style={{
                  display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: 6, marginBottom: 10,
                }}>
                  {activeFotos.map(f => (
                    <div
                      key={f.id}
                      style={{
                        position: 'relative', borderRadius: 6,
                        overflow: 'hidden', background: '#1a2035', aspectRatio: '4/3',
                      }}
                    >
                      {isVideo(f.url) ? (
                        <video
                          src={f.url} controls
                          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        />
                      ) : (
                        <img
                          src={f.url}
                          alt={activeLabel}
                          onClick={() => setLightbox(f.url)}
                          style={{
                            width: '100%', height: '100%',
                            objectFit: 'cover', display: 'block', cursor: 'zoom-in',
                          }}
                        />
                      )}
                      {!readonly && (
                        <button
                          onClick={() => handleDeleteRequest(f.id)}
                          style={{
                            position: 'absolute', top: 3, right: 3,
                            background: 'rgba(248,113,113,0.85)', border: 'none',
                            borderRadius: 5, padding: '2px 4px',
                            cursor: 'pointer', color: '#fff',
                            display: 'flex', alignItems: 'center',
                          }}
                          title="Remover"
                        >
                          <Trash2 size={10} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ fontSize: 11, color: '#475569', textAlign: 'center', padding: '8px 0', marginBottom: 8 }}>
                  Nenhuma foto em "{activeLabel}".
                </div>
              )}

              {/* Mensagem de erro de upload inline */}
              {uploadErr && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, fontSize: 11, color: '#f87171', marginBottom: 6 }}>
                  <span>{uploadErr}</span>
                  <button onClick={() => setUploadErr('')} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 13, lineHeight: 1 }}>×</button>
                </div>
              )}

              {/* Área de upload */}
              {!readonly && (
                <>
                  {/*
                    label htmlFor → ativa o input nativamente no browser/iOS/Android/PWA.
                    Mais confiável que div + fileRef.click() em iOS Safari standalone mode.
                    disabled={uploading} no input impede a abertura do seletor durante upload.
                  */}
                  <label
                    htmlFor={inputId}
                    onDragOver={e => { e.preventDefault(); setDragging(true); }}
                    onDragLeave={() => setDragging(false)}
                    onDrop={onDrop}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      border: `1px dashed ${dragging ? ACCENT : '#2d3748'}`,
                      borderRadius: 7, padding: '12px 12px', textAlign: 'center',
                      cursor: uploading ? 'wait' : 'pointer',
                      background: dragging ? 'rgba(52,211,153,0.06)' : 'transparent',
                      transition: 'all 0.12s',
                      minHeight: 44,
                      userSelect: 'none',
                    }}
                  >
                    <div style={{ fontSize: 12, color: '#64748b', pointerEvents: 'none' }}>
                      {uploading
                        ? 'Enviando...'
                        : `+ ${activeLabel} — clique ou arraste`}
                    </div>
                  </label>
                  <input
                    id={inputId}
                    ref={fileRef}
                    type="file"
                    multiple
                    accept="image/*,video/*"
                    disabled={uploading}
                    style={{ position: 'absolute', width: 0, height: 0, opacity: 0, overflow: 'hidden' }}
                    onChange={e => {
                      if (e.target.files?.length) uploadFiles(e.target.files);
                      e.target.value = '';
                    }}
                  />
                </>
              )}
            </>
          )}
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div
          onClick={() => setLightbox(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 99999, cursor: 'zoom-out',
          }}
        >
          <img
            src={lightbox}
            alt=""
            onClick={e => e.stopPropagation()}
            style={{
              maxWidth: '95vw', maxHeight: '95vh',
              objectFit: 'contain', borderRadius: 8,
            }}
          />
        </div>
      )}
    </div>
  );
}
